import { useState } from "react";
import { loginEditor, ApiError } from "../lib/pb";
import { useT } from "../lib/i18n";
import type { Editor } from "../lib/types";

export default function LoginPage({
  onLogin,
}: {
  onLogin: (e: Editor) => void;
}) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await loginEditor(email, password);
      if (!r.record.active) {
        setErr(t.loginErrorInactive);
        return;
      }
      onLogin(r.record as Editor);
    } catch (e) {
      const msg =
        e instanceof ApiError && e.status === 400
          ? t.loginErrorBad
          : t.loginErrorGeneric;
      setErr(msg);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        height: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "radial-gradient(1200px 600px at 50% -10%, rgba(184,134,74,0.10), transparent 60%), var(--bg-0)",
        padding: 24,
      }}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 28,
          animation: "rvm-page-fade 240ms ease",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 14,
          }}
        >
          <img
            src="/r-logo.png"
            alt="Reverium"
            width={104}
            height={104}
            style={{
              opacity: 0.96,
              userSelect: "none",
              pointerEvents: "none",
              filter: "drop-shadow(0 4px 16px rgba(0,0,0,0.45))",
            }}
          />
          <div
            style={{
              fontFamily: "var(--font-serif)",
              fontSize: 22,
              fontWeight: 600,
              letterSpacing: "-0.01em",
              color: "var(--text-0)",
            }}
          >
            Reverium <span style={{ color: "var(--text-2)" }}>Editorial</span>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          style={{
            width: 380,
            maxWidth: "92vw",
            padding: "30px 30px 28px",
            background: "var(--bg-1)",
            border: "1px solid var(--border-1)",
            borderRadius: "var(--radius-xl)",
            display: "flex",
            flexDirection: "column",
            gap: 18,
            boxShadow: "0 30px 80px rgba(0,0,0,0.45)",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <div
              style={{
                fontFamily: "var(--font-serif)",
                fontSize: 24,
                fontWeight: 600,
                letterSpacing: "-0.01em",
                color: "var(--text-0)",
                lineHeight: 1.2,
              }}
            >
              {t.loginTitle}
            </div>
            <p
              style={{
                fontSize: 13,
                color: "var(--text-2)",
                margin: 0,
                lineHeight: 1.5,
              }}
            >
              {t.loginSubtitle}
            </p>
          </div>

          <Field label={t.loginEmail}>
            <input
              type="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input"
            />
          </Field>

          <Field label={t.loginPassword}>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input"
            />
          </Field>

          {err && (
            <div
              style={{
                fontSize: 12,
                color: "var(--danger)",
                padding: "10px 12px",
                background: "var(--danger-soft)",
                border: "1px solid rgba(224,82,82,0.3)",
                borderRadius: "var(--radius-md)",
                lineHeight: 1.45,
              }}
            >
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy || !email || !password}
            className="btn btn-primary btn-lg"
            style={{ marginTop: 4 }}
          >
            {busy ? t.loginBtnBusy : t.loginBtn}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
    </label>
  );
}
